import numberUtils from '../utils/numberUtils';
import stringHelper from '../utils/stringHelper';

const PET_POLICY = 'petPolicy';

const NEGOTIABLE = 'negotiable';

const NOT_APPLICABLE = 'Not applicable';
const NOT_SET = 'Not set';

const TYPES_MAP = {
  INCOME_TO_RENT: 'incomeToRent',
  CREDIT_SCORE: 'creditScore',
  PET_POLICY_CAT_COUNT: 'petPolicyCatCount',
  PET_POLICY_DOG_COUNT: 'petPolicyDogCount',
  PET_POLICY_LARGE_DOG_COUNT: 'petPolicyLargeDogCount',
  PET_POLICY_SMALL_DOG_COUNT: 'petPolicySmallDogCount',
  PET_POLICY_DOG_SIZE: 'petPolicyDogSize',
  PET_POLICY_PET_DEPOSIT: 'petPolicyPetDeposit',
  PET_POLICY_PET_RENT: 'petPolicyPetRent',
} as const;

const STRING_VALUE_TO_NUMBER_VALUE_MAP = {
  '': 1,
  [NOT_APPLICABLE]: 1,
  '1.5x': 1.5,
  '1.75x': 1.75,
  '2x': 2,
  '2.25x': 2.25,
  '2.5x': 2.5,
  '2.75x': 2.75,
  '3x': 3,
  '3.25x': 3.25,
  '3.5x': 3.5,
  '3.75x': 3.75,
  '4x': 4,
} as Record<string, number>;

const LC_MIN_INCOME_TO_RENT_RATIO_OPTIONS = [
  { label: [NOT_SET], value: NOT_APPLICABLE },
  { label: '1.5x', value: '1.5x' },
  { label: '1.75x', value: '1.75x' },
  { label: '2x', value: '2x' },
  { label: '2.25x', value: '2.25x' },
  { label: '2.5x', value: '2.5x' },
  { label: '2.75x', value: '2.75x' },
  { label: '3x', value: '3x' },
  { label: '3.25x', value: '3.25x' },
  { label: '3.5x', value: '3.5x' },
  { label: '3.75x', value: '3.75x' },
  { label: '4x', value: '4x' },
];

const LC_MIN_CREDIT_SCORE_OPTIONS = [
  { label: [NOT_SET], value: NOT_APPLICABLE },
  // 500 - 735 with interval of 5
  ...Array.from({ length: (735 - 500) / 5 + 1 }, (_, i) => {
    const value = (500 + i * 5).toString();
    return { label: value, value };
  }),
  { label: '740+', value: '740+' },
];

// NOTE: `SLIDER_OPTIONS.ESCAPE` must be in accending order
const LC_CREDIT_SCORE_SLIDER_CONSTANTS = {
  '': {
    MARK_LABEL_OPTIONS: ['None', '500', '540', '580', '620', '660', '700', '740+'],
    SLIDER_OPTIONS: {
      MAX: 14,
      MIN: 0,
      STEP: 1,
      PAGE_FN: (step: number) => step * 2,
      BASELINE: 460,
      STEP_RATE: 20,
      ESCAPE: [1],
    },
    VALUE_TO_LABEL_MAP: {
      '': 0,
      [NOT_APPLICABLE]: 0,
      500: 2,
      520: 3,
      540: 4,
      560: 5,
      580: 6,
      600: 7,
      620: 8,
      640: 9,
      660: 10,
      680: 11,
      700: 12,
      720: 13,
      '740+': 14,
    },
  },
};

const CAT_COUNT_VALUES = {
  NO_CATS: 'Not allowed',
  ONE_CAT: '1 cat',
  TWO_CATS: '2 cats',
  THREE_PLUS_CATS: '3+ cats',
} as const;

const DOG_COUNT_VALUES = {
  NO_DOGS: 'Not allowed',
  ONE_DOG: '1 dog',
  TWO_DOGS: '2 dogs',
  THREE_PLUS_DOGS: '3+ dogs',
} as const;

const DOG_SIZE_VALUES = {
  '': undefined,
  SMALL: 'Small dogs - less than 20 lbs',
  MEDIUM: 'Medium dogs - less than 40 lbs',
  LARGE: 'Large dogs - over 40 lbs',
} as const;

export const THREE_PLUS = '3+';

const LC_PET_POLICY_FORM_CONSTANTS = {
  LABEL_TO_VALUE_MAP: {
    '': '',
    [CAT_COUNT_VALUES.NO_CATS]: '0',
    [CAT_COUNT_VALUES.ONE_CAT]: '1',
    [CAT_COUNT_VALUES.TWO_CATS]: '2',
    [CAT_COUNT_VALUES.THREE_PLUS_CATS]: THREE_PLUS,
    // No need to set DOG_COUNT_VALUES.NO_DOGS because it's already set with CAT_COUNT_VALUES.NO_CATS
    // as "Not allowed"
    // [DOG_COUNT_VALUES.NO_DOGS]: '0',
    [DOG_COUNT_VALUES.ONE_DOG]: '1',
    [DOG_COUNT_VALUES.TWO_DOGS]: '2',
    [DOG_COUNT_VALUES.THREE_PLUS_DOGS]: THREE_PLUS,
    [DOG_SIZE_VALUES.SMALL]: 'small',
    [DOG_SIZE_VALUES.MEDIUM]: 'medium',
    [DOG_SIZE_VALUES.LARGE]: 'large',
  },
  VALUE_TO_LABEL_MAP: {
    [`${TYPES_MAP.PET_POLICY_CAT_COUNT}0`]: CAT_COUNT_VALUES.NO_CATS,
    [`${TYPES_MAP.PET_POLICY_CAT_COUNT}1`]: CAT_COUNT_VALUES.ONE_CAT,
    [`${TYPES_MAP.PET_POLICY_CAT_COUNT}2`]: CAT_COUNT_VALUES.TWO_CATS,
    [`${TYPES_MAP.PET_POLICY_CAT_COUNT}3+`]: CAT_COUNT_VALUES.THREE_PLUS_CATS,
    [TYPES_MAP.PET_POLICY_CAT_COUNT]: '',
    [`${TYPES_MAP.PET_POLICY_DOG_COUNT}0`]: DOG_COUNT_VALUES.NO_DOGS,
    [`${TYPES_MAP.PET_POLICY_DOG_COUNT}1`]: DOG_COUNT_VALUES.ONE_DOG,
    [`${TYPES_MAP.PET_POLICY_DOG_COUNT}2`]: DOG_COUNT_VALUES.TWO_DOGS,
    [`${TYPES_MAP.PET_POLICY_DOG_COUNT}3+`]: DOG_COUNT_VALUES.THREE_PLUS_DOGS,
    [TYPES_MAP.PET_POLICY_DOG_COUNT]: '',
    [`${TYPES_MAP.PET_POLICY_DOG_SIZE}small`]: DOG_SIZE_VALUES.SMALL,
    [`${TYPES_MAP.PET_POLICY_DOG_SIZE}medium`]: DOG_SIZE_VALUES.MEDIUM,
    [`${TYPES_MAP.PET_POLICY_DOG_SIZE}large`]: DOG_SIZE_VALUES.LARGE,
    [TYPES_MAP.PET_POLICY_DOG_SIZE]: '',
  },
  CAT_COUNT_OPTIONS: Object.values(CAT_COUNT_VALUES),
  DOG_COUNT_OPTIONS: Object.values(DOG_COUNT_VALUES),
  DOG_SIZE_OPTIONS: Object.values(DOG_SIZE_VALUES).filter((value) => value !== undefined),
};

const TYPE_MAP_DISPLAY_VALUE_LC_PET_POLICY = {
  [TYPES_MAP.PET_POLICY_CAT_COUNT]: {
    label: 'Cats',
    getValue: (catCount: string, _: string) => {
      switch (catCount) {
        case '0':
          return 'Not allowed';
        case '':
          return 'Allowed';
        default:
          return catCount + stringHelper.pluralizeWord(Number(catCount[0]), ' cat', ' cats');
      }
    },
  },
  [TYPES_MAP.PET_POLICY_DOG_COUNT]: {
    label: 'Dogs',
    getValue: (dogCount: string, dogSize: string) => {
      if (dogCount === '0') {
        return 'Not allowed';
      }

      const dogSizeValue = DOG_SIZE_VALUES[(dogSize ? dogSize.toUpperCase() : dogSize) as keyof typeof DOG_SIZE_VALUES];

      const dogSizeSubtext = dogSizeValue?.split('-')?.pop()?.trim() || '';

      if (dogCount === '') {
        return dogSizeValue || 'Allowed';
      }

      return `${dogCount}${stringHelper.pluralizeWord(
        Number(dogCount[0]),
        ` ${dogSize ? dogSize + ' ' : ''}dog`,
        ` ${dogSize ? dogSize + ' ' : ''}dogs`,
      )}${dogSizeSubtext ? ` (${dogSizeSubtext})` : ''}`;
    },
  },
  [TYPES_MAP.PET_POLICY_SMALL_DOG_COUNT]: {
    label: 'Small Dogs',
    getValue: (dogCount: string, dogSize: string) => {
      if (dogCount === '0') {
        return 'Not allowed';
      }

      const dogSizeValue = DOG_SIZE_VALUES[(dogSize ? dogSize.toUpperCase() : dogSize) as keyof typeof DOG_SIZE_VALUES];

      const dogSizeSubtext = dogSizeValue?.split('-')?.pop()?.trim() || '';

      if (dogCount === '') {
        return dogSizeValue || 'Allowed';
      }

      return `${dogCount}${stringHelper.pluralizeWord(
        Number(dogCount[0]),
        ` ${dogSize ? dogSize + ' ' : ''}dog`,
        ` ${dogSize ? dogSize + ' ' : ''}dogs`,
      )}${dogSizeSubtext ? ` (${dogSizeSubtext})` : ''}`;
    },
  },
  [TYPES_MAP.PET_POLICY_LARGE_DOG_COUNT]: {
    label: 'Large Dogs',
    getValue: (dogCount: string, dogSize: string) => {
      if (dogCount === '0') {
        return 'Not allowed';
      }

      const dogSizeValue = DOG_SIZE_VALUES[(dogSize ? dogSize.toUpperCase() : dogSize) as keyof typeof DOG_SIZE_VALUES];

      const dogSizeSubtext = dogSizeValue?.split('-')?.pop()?.trim() || '';

      if (dogCount === '') {
        return dogSizeValue || 'Allowed';
      }

      return `${dogCount}${stringHelper.pluralizeWord(
        Number(dogCount[0]),
        ` ${dogSize ? dogSize + ' ' : ''}dog`,
        ` ${dogSize ? dogSize + ' ' : ''}dogs`,
      )}${dogSizeSubtext ? ` (${dogSizeSubtext})` : ''}`;
    },
  },
  [TYPES_MAP.PET_POLICY_PET_DEPOSIT]: {
    label: 'Pet deposit',
    getValue: (petDeposit: string, _: string) => numberUtils.asCurrency(Number(petDeposit)),
  },
  [TYPES_MAP.PET_POLICY_PET_RENT]: {
    label: 'Pet rent',
    getValue: (petRent: string, _: string) => numberUtils.asCurrency(Number(petRent)),
  },
  [NEGOTIABLE]: {
    label: 'Flexibility',
    getValue: (negotiable: boolean | null, fallback: string) => {
      if (negotiable === null) {
        return fallback;
      }

      return negotiable ? 'Negotiable' : 'Non-negotiable';
    },
  },
} as const;

const TYPES_MAP_KEYS = Object.keys(TYPES_MAP);
const TYPES_MAP_VALUES = Object.values(TYPES_MAP);

export {
  NOT_APPLICABLE,
  NOT_SET,
  NEGOTIABLE,
  PET_POLICY,
  STRING_VALUE_TO_NUMBER_VALUE_MAP,
  TYPES_MAP,
  TYPES_MAP_KEYS,
  TYPE_MAP_DISPLAY_VALUE_LC_PET_POLICY,
  TYPES_MAP_VALUES,
  LC_CREDIT_SCORE_SLIDER_CONSTANTS,
  LC_MIN_CREDIT_SCORE_OPTIONS,
  LC_MIN_INCOME_TO_RENT_RATIO_OPTIONS,
  LC_PET_POLICY_FORM_CONSTANTS,
};
